import { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { visitPageGA } from '@sonifi/wb-core/services/ga.service';
import { buildTabs } from './helper/navHelper';
import { defaultTabs } from './helper/tabs';
import { useWbNavBlock } from './useWbNavBlock';

export const useWbNav = ({ primaryNav, secondaryNav }) => {
  const { authenticated, user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Memoize tabs calculation
  const tabs = useMemo(() => {
    if (!authenticated || !user) return [];
    return buildTabs(defaultTabs, user);
  }, [authenticated, user]);

  const [navigationBar, setNavigationBar] = useState(() => {
    const primaryNavState = tabs.find((tab) => tab.key === primaryNav);
    const secondaryNavState = primaryNavState?.navChildren.find(
      (nv) => nv.key === secondaryNav,
    );
    return {
      primaryNavState,
      secondaryNavState,
    };
  });

  // Tracking values to avoid re-renders
  const refPreviousValues = useRef({ navigationBar });

  useWbNavBlock({
    onRestoreNav: () => {
      setNavigationBar(refPreviousValues.current.navigationBar);
    },
  });

  const onPrimaryNavClick = (tab) => {
    // Update previous values
    refPreviousValues.current.navigationBar = navigationBar;
    // Update current values
    setNavigationBar({ primaryNavState: tab, secondaryNavState: null });
  };

  const onSecondaryNavClick = (nv) => {
    // Update previous values
    refPreviousValues.current.navigationBar = navigationBar;
    // Update current values
    setNavigationBar((prev) => ({ ...prev, secondaryNavState: nv }));
    dispatch(visitPageGA({ page: nv.url, key: nv.key }));
    navigate(nv.url);
  };

  return {
    authenticated,
    onPrimaryNavClick,
    onSecondaryNavClick,
    primaryNavState: navigationBar.primaryNavState,
    secondaryNavState: navigationBar.secondaryNavState,
    tabs,
  };
};
